import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { ArrowLeft, ArrowUpRight, MapPin, Phone, Bed, UtensilsCrossed, PartyPopper } from "lucide-react";

const PLACEHOLDER = "https://images.pexels.com/photos/164586/pexels-photo-164586.jpeg?auto=compress&cs=tinysrgb&w=1200";

export default function CityPage() {
  const { state, city } = useParams();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.listHotels({ state, city }).then(setHotels).finally(() => setLoading(false));
  }, [state, city]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
      <Link
        to={`/state/${encodeURIComponent(state)}`}
        data-testid="back-to-state"
        className="inline-flex items-center gap-2 font-label text-xs text-[var(--ink-soft)] hover:text-[var(--ink)] mb-6 sm:mb-8"
      >
        <ArrowLeft className="w-3 h-3" /> Back to {state}
      </Link>

      <div className="mb-8 sm:mb-12">
        <div className="font-label text-xs text-[var(--brand-orange)] mb-3 sm:mb-4">
          {state} · {hotels.length} {hotels.length === 1 ? "Hotel" : "Hotels"}
        </div>
        <h1 data-testid="city-title" className="font-display text-4xl sm:text-6xl md:text-7xl leading-none">
          {city}.
        </h1>
      </div>

      {loading ? (
        <div className="text-[var(--ink-soft)] font-label text-sm">Loading…</div>
      ) : hotels.length === 0 ? (
        <div className="border-2 border-dashed border-[var(--line)] p-10 text-center">
          <div className="font-label text-xs text-[var(--brand-blue)] mb-3">No hotels</div>
          <h3 className="font-display text-2xl sm:text-3xl">Nothing here yet.</h3>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
          {hotels.map((h, i) => (
            <HotelCard key={h.id} h={h} idx={i} />
          ))}
        </div>
      )}
    </div>
  );
}

function HotelCard({ h, idx }) {
  const img = h.image_url ? (h.image_url.startsWith("http") ? h.image_url : fileUrl(h.image_url)) : PLACEHOLDER;
  const upcoming = h.status === "upcoming";
  return (
    <article
      data-testid={`hotel-card-${h.id}`}
      className="fade-up group border border-[var(--ink)] bg-white overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-[2px] hover:shadow-[10px_10px_0px_0px_var(--ink)]"
      style={{ animationDelay: `${idx * 60}ms` }}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-[var(--paper-3)]">
        <img
          src={img}
          alt={h.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
        />
        <div className="absolute top-3 left-3 bg-white px-3 py-1 font-label text-[10px]">{h.state}</div>
        {upcoming && (
          <div
            data-testid={`hotel-upcoming-badge-${h.id}`}
            className="absolute top-3 right-3 bg-[var(--brand-orange)] text-white px-3 py-1 font-label text-[10px]"
          >
            Upcoming
          </div>
        )}
      </div>
      <div className="p-5 sm:p-6 flex-1 flex flex-col">
        <h3 className="font-display text-xl sm:text-2xl leading-tight mb-2 group-hover:text-[var(--brand-blue)] transition-colors">
          {h.name}
        </h3>
        <div className="flex items-start gap-2 text-sm text-[var(--ink-soft)] mb-4">
          <MapPin className="w-3.5 h-3.5 mt-[3px] shrink-0" />
          <span>{h.location}</span>
        </div>
        {h.description && (
          <p className="text-sm text-[var(--ink-soft)] mb-5 line-clamp-3">{h.description}</p>
        )}

        <div className="grid grid-cols-3 gap-2 border-t border-[var(--line)] pt-4 mb-5">
          <MetricBox icon={<Bed className="w-3.5 h-3.5" />} label="Rooms" value={h.rooms} />
          <MetricBox icon={<PartyPopper className="w-3.5 h-3.5" />} label="Banquets" value={h.banquet_halls} />
          <MetricBox icon={<UtensilsCrossed className="w-3.5 h-3.5" />} label="F&B" value={h.restaurants} />
        </div>

        {h.phone && (
          <a
            href={`tel:${h.phone}`}
            data-testid={`hotel-phone-${h.id}`}
            className="inline-flex items-center gap-2 text-xs text-[var(--ink-soft)] hover:text-[var(--brand-blue)] mb-4"
          >
            <Phone className="w-3 h-3" /> {h.phone}
          </a>
        )}

        <div className="mt-auto">
          {h.external_url ? (
            <a
              href={h.external_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`view-hotel-btn-${h.id}`}
              className="inline-flex items-center justify-between w-full bg-[var(--ink)] text-white px-4 py-3 font-display text-sm hover:bg-[var(--brand-blue)] transition-colors"
            >
              View Hotel Details <ArrowUpRight className="w-4 h-4" />
            </a>
          ) : (
            <button
              disabled
              data-testid={`view-hotel-btn-disabled-${h.id}`}
              className="inline-flex items-center justify-between w-full bg-[var(--paper-3)] text-[var(--ink-soft)] px-4 py-3 font-display text-sm cursor-not-allowed"
            >
              No External Link
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

function MetricBox({ icon, label, value }) {
  return (
    <div className="text-center">
      <div className="inline-flex items-center gap-1 justify-center text-[var(--ink-soft)] mb-1">{icon}</div>
      <div className="font-display text-lg sm:text-xl leading-none">{value}</div>
      <div className="font-label text-[9px] text-[var(--ink-soft)] mt-1">{label}</div>
    </div>
  );
}
